import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CollapsibleGroupProps {
  icon: React.ReactNode;
  title: string;
  count?: number;
  summary?: string;
  defaultExpanded?: boolean;
  onAdd?: () => void;
  addButtonLabel?: string;
  children?: React.ReactNode;
  isEmpty?: boolean;
  customAddButton?: React.ReactNode;
}

export const CollapsibleGroup: React.FC<CollapsibleGroupProps> = ({
  icon,
  title,
  count = 0,
  summary,
  defaultExpanded = false,
  onAdd,
  addButtonLabel = 'Add',
  children,
  isEmpty = true,
  customAddButton,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full px-4 py-3 flex items-center justify-between",
          "hover:bg-gray-50 transition-colors duration-200",
          "focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
        )}
      >
        <div className="flex items-center gap-3">
          {/* Expand/Collapse Icon */}
          <div className="text-gray-400">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </div>

          {/* Group Icon */}
          <div className="text-gray-600">{icon}</div>

          {/* Title */}
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>

          {/* Count Badge */}
          {count > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
              {count}
            </span>
          )}
        </div>

        {/* Summary (when collapsed) */}
        {!isExpanded && summary && (
          <div className="text-sm text-gray-600 font-medium">{summary}</div>
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4 space-y-4">
            {/* Children */}
            {children && (
              <div className="space-y-3">{children}</div>
            )}

            {/* Add Button */}
            {customAddButton ? (
              customAddButton
            ) : onAdd ? (
              <Button
                onClick={onAdd}
                variant="outline"
                size="sm"
                className="w-full border-dashed border-2 hover:border-purple-400 hover:bg-purple-50 hover:text-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                {addButtonLabel}
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
